import { httpRequestLogsApi } from 'app/services/api';
import { blobCacheService } from 'app/services/cache';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { buildRequestParams, parseLogsResponse } from './devTools.actions.utils';

export const BLOB_EXPIRED_MARKER = 'BLOB_EXPIRED';
const BLOB_NOT_FOUND_MARKER = 'BLOB_NOT_FOUND';

const HTTP_STATUS_NOT_FOUND = 404;

export const DEV_TOOLS_ACTION_TYPE = createNamedActions('devTools', {
  GET_HTTP_REQUEST_LOGS: 'GET_HTTP_REQUEST_LOGS',
  APPEND_HTTP_REQUEST_LOGS: 'APPEND_HTTP_REQUEST_LOGS',
  SELECT_LOG: 'SELECT_LOG',
});

export const loadHttpRequestLogsAction = catchAsyncActionError(
  ({ take, ...filters } = {}) =>
    async dispatch => {
      const params = buildRequestParams(filters, 0, take);
      const { data } = await httpRequestLogsApi.getHttpRequestLogs(params);
      const { logs, total } = parseLogsResponse(data);

      dispatch({ type: DEV_TOOLS_ACTION_TYPE.GET_HTTP_REQUEST_LOGS, payload: { logs, total } });
    },
);

export const loadMoreHttpRequestLogsAction = catchAsyncActionError(
  ({ skip = 0, take, ...filters } = {}) =>
    async dispatch => {
      const params = buildRequestParams(filters, skip, take);
      const { data } = await httpRequestLogsApi.getHttpRequestLogs(params);
      const { logs, total } = parseLogsResponse(data);

      dispatch({ type: DEV_TOOLS_ACTION_TYPE.APPEND_HTTP_REQUEST_LOGS, payload: { logs, total } });
    },
);

const inFlightRequests = new Map();

export const getBlobAction = catchAsyncActionError(({ blobStorageId }) => async () => {
  if (!blobStorageId) {
    return null;
  }

  const cached = await blobCacheService.get(blobStorageId);

  if (cached) {
    return cached;
  }

  if (inFlightRequests.has(blobStorageId)) {
    return inFlightRequests.get(blobStorageId);
  }

  const fetchBlobFromApi = async () => {
    const { data } = await httpRequestLogsApi.getBlobUrl({ blobStorageId });

    if (data?.expiresOn && new Date(data.expiresOn) < new Date()) {
      return { blobStatus: BLOB_EXPIRED_MARKER, expiresOn: data.expiresOn };
    }

    try {
      return await httpRequestLogsApi.fetchBlobContent(data?.url);
    } catch (error) {
      if (error?.response?.status === HTTP_STATUS_NOT_FOUND) {
        return { blobStatus: BLOB_NOT_FOUND_MARKER };
      }

      throw error;
    }
  };

  const fetchPromise = fetchBlobFromApi();

  inFlightRequests.set(blobStorageId, fetchPromise);

  try {
    const content = await fetchPromise;

    await blobCacheService.set(blobStorageId, content);

    return content;
  } finally {
    inFlightRequests.delete(blobStorageId);
  }
});

export const selectLogAction = logId => dispatch => {
  return dispatch({
    type: DEV_TOOLS_ACTION_TYPE.SELECT_LOG,
    payload: logId,
  });
};
